// DryRunStep.jsx — the exact POST /v1/custom-fields bodies, in the exact
// order they would be sent, each with its deterministic validation verdict
// against freshly-fetched post-options. This is the DEFAULT end of the flow:
// through Milestone 2 there is no execute button at all — the push engine,
// ledger, and rollback arrive in Milestone 3. The bodies can be downloaded
// as JSON for records. Navigation back to the builder is free (Decision #15).

import { T } from '../../theme';
import { Button, Badge } from '../fmxkit';
import { fieldsetLabel } from '../../fieldBuilder/fieldsets';
import { formulaChecklist } from '../../fieldBuilder/visibilityFormula';
import CopyFormulaButton from './CopyFormulaButton';

export default function DryRunStep({ session, draft, bodies, validation, siteUrl, onBack }) {
  const fieldsById = new Map((draft?.fields || []).map(f => [f.id, f]));
  const ready = bodies.filter(b => (validation.get(b.draftId)?.errors || []).length === 0);
  const blocked = bodies.filter(b => (validation.get(b.draftId)?.errors || []).length > 0);
  // Only formulas on fields that will actually be created.
  const readyIds = new Set(ready.map(b => b.draftId));
  const formulas = formulaChecklist((draft?.fields || []).filter(f => readyIds.has(f.id)));

  const download = () => {
    const payload = {
      site: siteUrl,
      session: session?.id,
      generatedAt: new Date().toISOString(),
      note: 'Dry-run preview — these bodies have NOT been sent to FMX.',
      posts: ready.map(b => ({ endpoint: 'POST /v1/custom-fields', body: b.body })),
      blocked: blocked.map(b => ({
        body: b.body,
        errors: validation.get(b.draftId)?.errors || [],
      })),
      // Not sendable — FMX's API has no visibility-formula property. Kept in
      // the download so the manual follow-up survives closing the tab.
      visibilityFormulasToApplyByHand: formulas,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `field-builder-dry-run-${(session?.id || 'session').slice(0, 8)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div style={{ background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusCard, padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <div style={{ font: `700 18px/24px ${T.fontSans}`, color: T.ink }}>Dry run — nothing will be sent</div>
          <div style={{ font: `400 13px/19px ${T.fontSans}`, color: T.inkMuted, marginTop: 3, maxWidth: 680 }}>
            These are the exact bodies that would be POSTed to <b>{siteUrl}</b>/api/v1/custom-fields,
            in creation order, validated against the site’s live post-options fetched just now.
            Creating fields is not part of this build — the execute engine (with its ledger and
            rollback) ships in Milestone 3.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <Button variant="neutral" icon="arrow-left" onClick={onBack}>Back to builder</Button>
          <Button variant="secondary" icon="download" onClick={download} disabled={bodies.length === 0}>Download JSON</Button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, margin: '14px 0' }}>
        <Badge color="green" icon="check">{ready.length} ready</Badge>
        {blocked.length > 0 && <Badge color="red" icon="circle-exclamation">{blocked.length} blocked</Badge>}
      </div>

      {bodies.length === 0 && <p style={{ color: T.inkSoft, fontSize: 13 }}>No fields to preview.</p>}

      {ready.map((b, i) => {
        const f = fieldsById.get(b.draftId);
        const warnings = validation.get(b.draftId)?.warnings || [];
        return (
          <div key={b.draftId} style={{ border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusCard, marginBottom: 10, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: T.bgMuted }}>
              <span style={{ font: `700 12px/17px ${T.fontData}`, color: T.inkMuted }}>{i + 1}</span>
              <span style={{ font: `600 13px/19px ${T.fontSans}`, color: T.ink, flex: 1 }}>{f?.name}</span>
              <span style={{ font: `400 12px/17px ${T.fontSans}`, color: T.inkMuted }}>{fieldsetLabel(f?.fieldset)}</span>
              <Badge color="green" icon="check">valid</Badge>
            </div>
            {warnings.map((w, wi) => (
              <div key={wi} style={{ padding: '6px 12px 0', font: `400 12px/17px ${T.fontSans}`, color: T.orangeText }}>
                <i className="fa-solid fa-triangle-exclamation" /> {w}
              </div>
            ))}
            <pre style={{ margin: 0, padding: '10px 12px', background: T.codeBg, color: T.codeFg, font: `400 12px/18px ${T.fontData}`, overflowX: 'auto' }}>
              {JSON.stringify(b.body, null, 2)}
            </pre>
          </div>
        );
      })}

      {/* Visibility formulas are NOT in any body above — FMX's API cannot set
          them (the POST/PUT write model has no such property). This is the
          handoff: push the fields, then work down this list in FMX. In
          Milestone 3 the same list persists on the results screen, tied to the
          actual push. */}
      {formulas.length > 0 && (
        <div style={{ marginTop: 18, border: `1px solid ${T.badge.blueLight}`, borderRadius: T.radiusCard, overflow: 'hidden' }}>
          <div style={{ padding: '10px 12px', background: T.tint.info }}>
            <div style={{ font: `700 13px/19px ${T.fontSans}`, color: T.brand }}>
              <i className="fa-solid fa-eye" /> Apply these {formulas.length} visibility formula{formulas.length === 1 ? '' : 's'} in FMX after pushing
            </div>
            <div style={{ font: `400 12px/18px ${T.fontSans}`, color: T.inkMuted, margin: '3px 0 8px', maxWidth: 680 }}>
              FMX’s API can’t set visibility formulas, so they aren’t part of the bodies above and
              nothing here is sent. Create the fields first, then open each field in FMX
              (<b>Admin Settings → Custom fields</b>) and paste its formula into the visibility
              formula box.
            </div>
            <CopyFormulaButton
              className="fmx-btn-xs-rule"
              label={`Copy all ${formulas.length} as a checklist`}
              text={formulas.map(f => `${f.fieldName}: ${f.formula}`).join('\n')}
            />
          </div>
          {formulas.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderTop: `1px solid ${T.borderSubtle}` }}>
              <span style={{ font: `600 12px/17px ${T.fontSans}`, color: T.ink, width: 200, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {f.fieldName}
              </span>
              <code style={{ flex: 1, font: `400 12px/18px ${T.fontData}`, color: T.inkMuted, minWidth: 0, wordBreak: 'break-all' }}>
                {f.formula}
              </code>
              <CopyFormulaButton text={f.formula} label="Copy" style={{ flexShrink: 0 }} />
            </div>
          ))}
        </div>
      )}

      {blocked.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ font: `700 14px/20px ${T.fontSans}`, color: T.red500, marginBottom: 8 }}>
            Held back — failing validation ({blocked.length})
          </div>
          {blocked.map((b) => {
            const f = fieldsById.get(b.draftId);
            const errors = validation.get(b.draftId)?.errors || [];
            return (
              <div key={b.draftId} style={{ border: `1px solid ${T.badge.red}`, background: T.tint.error, borderRadius: T.radiusCard, marginBottom: 8, padding: '8px 12px' }}>
                <div style={{ font: `600 13px/19px ${T.fontSans}`, color: T.ink }}>
                  {f?.name} <span style={{ font: `400 12px/17px ${T.fontSans}`, color: T.inkMuted }}>· {fieldsetLabel(f?.fieldset)}</span>
                </div>
                {errors.map((e, ei) => (
                  <div key={ei} style={{ font: `400 12px/18px ${T.fontSans}`, color: T.redDarker, marginTop: 2 }}>
                    <i className="fa-solid fa-circle-exclamation" /> {e}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
